<script setup lang="ts">
/**
 * Singapura — AR Verification of Data. Studio panel: live 단일 문서 + create task.
 *   cdd/CddVarField/useCddFill 는 aura/agm 재사용. 단일 문서라 인물별 스코프·서명자 없음.
 *   반복부(이사/주주/임원/담보)는 문서 내 loop 로 렌더 → normalize 가 배열로 변환.
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, typeOf } from '../../aura/agm/cdd/useCddFill';
import VerificationPackageDoc from '../verification/VerificationPackageDoc.vue';
import { normalizeSingapuraVerificationForWord } from '../utils/normalizeSingapuraVerificationForWord';
import { toLongDate } from '../utils/longDate';
import { formatAppliedAddress } from '../utils/formatAppliedAddress';
import { V_LOOPS, V_SHARE_CAPITAL, V_CHARGES } from '../verification/verifyLoops';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { loadCmBaseCodeOptions, nationalityLabelForOutput } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill({ formatDate: toLongDate }); // 화면 날짜도 "30 June 2026" (고객 요청 2026-10-15)
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
// Edit 재진입: 저장값 복원 후엔 회사 자동채움(비동기, 복원보다 늦게 끝남)이 직접 고친 값·인원수(행)를 덮지 않게 한다.
//   빈 칸만 채운다. 다른 회사로 바꾼 경우에만 새 회사 값으로 다시 채운다.
const restoredFromSaved = ref(false);
const taskConfigSteps = ref<string[]>([]);
const TASK_CODE = 'CSI001';

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide('singapuraVerifyEntityCd', entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({ get: () => taskTitleLocal.value || props.taskTitle || '', set: (v: string) => { taskTitleLocal.value = v; } });
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

function isoDate(v: unknown): string {
  const s = String(v ?? '').trim();
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  // 직원 API(CONVERT 103)는 dd/mm/yyyy → ISO 로 (날짜 칸·문서 치환은 ISO 기준)
  const d = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return d ? `${d[3]}-${d[2].padStart(2, '0')}-${d[1].padStart(2, '0')}` : s;
}

// ── person / address lookup 배선 ─────────────────────────────
function lookupRoleFor(bare: string): { role: string; scoped: boolean } {
  if (/^director_\d+_name$/.test(bare) || bare === 'confirming_director_name') return { role: 'DIRECTOR', scoped: true };
  if (/^shareholder_\d+_name$/.test(bare)) return { role: 'SHAREHOLDER', scoped: true };
  if (/^company_secretary_\d+_name$/.test(bare)) return { role: 'SECRETARY', scoped: true };
  if (/^md_\d+_name$/.test(bare)) return { role: 'MANAGING_DIRECTOR', scoped: true };
  if (/^ceo_\d+_name$/.test(bare)) return { role: 'CEO', scoped: true };
  if (/^auditor_\d+_firm_name$/.test(bare)) return { role: 'AUDITOR', scoped: true };
  if (bare === 'to_director_name') return { role: 'DIRECTOR', scoped: true };
  return { role: 'CONTACT', scoped: false };
}
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    const { role, scoped } = lookupRoleFor(bareKey(key));
    emit('request-person-lookup', { fieldGroup: 'singapura-verification', key: bareKey(key), scopedKey: key, defaultRole: role, lookupType: 'officer', entityCd: scoped ? entityCd.value : '' });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

watch(() => props.selectedPerson, (p) => {
  if (!p || !fill.activeKey.value) return;
  const key = fill.activeKey.value;
  fill.applyPersonToActive(p as Record<string, any>);
  emitProgress();
  // ✨ 로 고른 인물 — 공용 엔진은 이 문서와 칸 이름이 달라(identification_no/email 등) 이름·주소만 바뀐다.
  //   → 자동채움과 같은 규칙(컨택트 보강 + 인물 값)으로 그 행의 인물 칸을 새 사람 값으로 전부 교체.
  const m = bareKey(key).match(/^(director|md|ceo|company_secretary|shareholder)_(\d+)_name$/);
  if (m) void applyPickedPersonRow(key, m[1], p as Record<string, any>, fill.getValue(key));
});
/** 반복부 한 행(인물 칸)을 ✨ 로 고른 사람 값으로 교체. 값이 없는 칸은 비워 이전 사람 값이 남지 않게 한다. */
async function applyPickedPersonRow(nameKey: string, prefix: string, p: Record<string, any>, pickedName: string) {
  const base = nameKey.replace(/_name$/, '');
  const fromPerson: Record<string, string> = {
    residential_address: st(p.residentialAddress || p.primaryAddress || p.address),
    id_no: st(p.contactRegNo || p.nricNo || p.passportNo || p.idNo),
    nationality: st(p.nationalityName || p.nationality),
    email: st(p.contactEmail || p.email),
    mobile: [st(p.areaCode), st(p.mobileNo)].filter(Boolean).join(' '),
    appointment_date: isoDate(p.appointmentDate),
  };
  const contactCd = st(p.contactCd || p.contactCode || p.CONTACT_CD);
  const enriched = contactCd ? await enrichContact(contactCd) : {};
  if (fill.getValue(nameKey) !== pickedName) return; // 조회 중에 그 칸이 다른 사람으로 바뀜
  const merged: Record<string, string> = { ...enriched };
  for (const [k, v] of Object.entries(fromPerson)) if (v) merged[k] = v;
  if (enriched.residential_address) merged.residential_address = enriched.residential_address;
  const fields = V_LOOPS.find((l) => l.prefix === prefix)?.fields.map((f) => f.s) ?? [];
  for (const f of fields) {
    if (f === 'name' || f === 'shares_held' || f === 'percent') continue;
    fill.setValue(`${base}_${f}`, merged[f] || '');
  }
  emitProgress();
}
watch(() => props.selectedAddress, (a) => {
  if (!a?.value || !fill.activeKey.value) return;
  fill.setValue(fill.activeKey.value, a.value); emitProgress();
});
watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  // SG 입력은 line 과 우편번호·국가가 따로 온다 → "…, Singapore 059818" 까지 붙인다.
  const line = formatAppliedAddress(a);
  if (line) { fill.setValue(a.key, line); emitProgress(); }
}, { deep: true });

const st = (v: unknown) => String(v ?? '').trim();
/** 컨택트 주소 파트 → 한 줄. 해외주소 우선, 없으면 SG 주소. alt=true 면 대체주소(ALT_*, 문서의 Contact address). */
function buildAddress(c: any, alt = false): string {
  if (!c || typeof c !== 'object') return '';
  const g = (...ks: string[]) => { for (const k of ks) { const v = st((c as any)[k]); if (v) return v; } return ''; };
  const f = (camel: string, upper: string) => (alt
    ? g(`alt${camel.charAt(0).toUpperCase()}${camel.slice(1)}`, `ALT_${upper}`)
    : g(camel, upper));
  const fl1 = f('foreignAddressLine1', 'FOREIGN_ADDRESS_LINE_1'); const fl2 = f('foreignAddressLine2', 'FOREIGN_ADDRESS_LINE_2');
  if (fl1 || fl2) return [fl1, fl2].filter(Boolean).join(', ');
  const block = f('blockNo', 'BLOCK_NO'); const street = f('streetName', 'STREET_NAME'); const building = f('buildingName', 'BUILDING_NAME');
  const level = f('addrLevel', 'ADDR_LEVEL'); const unitNo = f('addrUnit', 'ADDR_UNIT'); const postal = f('postalCode', 'POSTAL_CODE');
  const unit = [level, unitNo].filter(Boolean).join('-');
  return [[block, street].filter(Boolean).join(' '), building, unit ? `#${unit}` : '', postal ? `Singapore ${postal}` : ''].filter(Boolean).join(', ');
}
async function enrichContact(contactCd: string): Promise<Record<string, string>> {
  const cd = st(contactCd); if (!cd) return {};
  try {
    const raw: any = await contactsApi.getContactById(cd);
    const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
    const out: Record<string, string> = {};
    // 거주지 = 기본 주소, Contact address = 대체주소(ALT_*). 대체주소가 없으면 비워 둔다(거주지 복사 X).
    const addr = buildAddress(c); if (addr) out.residential_address = addr;
    const altAddr = buildAddress(c, true); if (altAddr) out.contact_address = altAddr;
    const email = st(c.email || c.CONTACT_EMAIL || c.contactEmail); if (email) out.email = email;
    const mobile = [st(c.areaCode || c.AREA_CODE), st(c.mobileNo || c.MOBILE_NO)].filter(Boolean).join(' '); if (mobile) out.mobile = mobile;
    // 국적은 코드(NATIONALITY_CD)로 저장 → B04 basecode 로 이름 변환. 없으면 이름 필드 폴백.
    const natCd = st(c.nationalityCd || c.NATIONALITY_CD);
    const natName = st(c.nationalityName || c.NATIONALITY_NAME || c.nationality);
    const nat = natCd ? (nationalityLabelForOutput(natCd) || natName || natCd) : natName;
    if (nat) out.nationality = nat;
    const id = st(c.contactRegNo || c.CONTACT_REG_NO || c.nricNo || c.passportNo); if (id) out.id_no = id;
    return out;
  } catch { return {}; }
}

// ── 자동채움 — 회사·주소·FYE + 이사/비서/주주(명수만큼) + 주식수·지분%·신원 보강 ──
async function autofillFromEntity(cdRaw: string) {
  const cd = st(cdRaw);
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  if (entityChanged) {
    restoredFromSaved.value = false;
    // 다른 회사로 바꾸면 이전 회사의 인물·자본·채무 행(직접 추가/수정한 행 포함)과 인원수를 모두 비운다 —
    //   새 회사에 그 직책이 없으면(예: CEO·감사인) 이전 회사 행이 그대로 남던 문제.
    const loops = [...V_LOOPS, V_SHARE_CAPITAL, V_CHARGES];
    for (const k of Object.keys(fill.values)) {
      if (loops.some((l) => k === l.countKey || k.startsWith(`${l.prefix}_`))) fill.setValue(k, '');
    }
  }
  lastAutofillCd.value = cd;
  await loadCmBaseCodeOptions('B04').catch(() => { /* 국적 라벨 매핑용 — 실패해도 진행 */ });
  if (!restoredFromSaved.value) for (const k of autofilledKeys.value) fill.setValue(k, '');
  const written = new Set<string>();
  const set = (k: string, v: unknown) => {
    const s = st(v);
    if (restoredFromSaved.value && st(fill.values[k])) return; // 저장값 보존(인원수 *_count 포함)
    if (s) { fill.setValue(k, s); written.add(k); }
  };

  /** rows: [{name, id_no?, nationality?, appointment_date?, shares_held?, percent?, contactCd?}] → 명수만큼 필드 시드 + 컨택트 보강. */
  async function seedPeople(prefix: string, countKey: string, rows: Array<Record<string, string>>) {
    const named = rows.filter((r) => st(r.name) || st(r.firm_name));
    if (!named.length) return;
    // 컨택트 보강값 위에 officer 행의 **비어있지 않은** 값만 덮는다(빈 officer 값이 보강값을 지우지 않게).
    const enriched = await Promise.all(named.map(async (r) => {
      const base = r.contactCd ? await enrichContact(r.contactCd) : {};
      const merged: Record<string, string> = { ...base };
      for (const [k, v] of Object.entries(r)) if (st(v)) merged[k] = v as string;
      return merged;
    }));
    set(countKey, String(enriched.length));
    // 문서 반복부에 있는 칸만 채운다(감사인은 address 하나 — 컨택트 주소를 그 칸으로).
    const fields = V_LOOPS.find((l) => l.prefix === prefix)?.fields.map((f) => f.s) ?? [];
    enriched.forEach((r, i) => {
      const idx = i + 1;
      if (prefix === 'auditor' && !st(r.address)) r.address = r.residential_address || '';
      for (const f of fields) {
        if (r[f] != null && st(r[f])) set(`${prefix}_${idx}_${f}`, r[f]);
      }
    });
  }

  // 1) 회사·주소·FYE + 주주(members)
  let members: any[] = [];
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_uen', src.registrationNo);
    set('financial_year_end', isoDate(src.financialYearEnd));
    if (src.registeredOfficeAddress) set('registered_office_address', src.registeredOfficeAddress);
    // 등록사무소 주소 효력일 — 엔티티 상세의 Effective date(L_ENTITY.ADDR_EFFECTIVE_DATE)
    if (src.addrEffectiveDate) set('registered_office_effective_date', isoDate(src.addrEffectiveDate));
    // 주요사업(SSIC) — 코드 + 설명(L_SSIC.SSIC_TITLE). 설명이 없고 값이 "코드 - 설명" 형태면 분리.
    const setSsic = (code: string, desc: string, codeKey: string, descKey: string) => {
      const c = st(code); const d = st(desc);
      if (d) { if (c) set(codeKey, c); set(descKey, d); return; }
      if (!c) return;
      const m = c.match(/^(\d{3,5})\s*[-–:]\s*(.+)$/);
      if (m) { set(codeKey, m[1]); set(descKey, m[2].trim()); } else set(codeKey, c);
    };
    setSsic(src.primaryActivity, src.primaryActivityDesc, 'primary_ssic_code', 'primary_ssic_desc');
    setSsic(src.secondaryActivity, src.secondaryActivityDesc, 'secondary_ssic_code', 'secondary_ssic_desc');
    // 주식자본 — 통화별 블록(여러 개 가능) → 반복부 시드.
    const caps: any[] = Array.isArray(src.shareCapitals) ? src.shareCapitals : [];
    if (caps.length) {
      set('share_capital_count', String(caps.length));
      const numFmt = (v: unknown) => (Number(v) > 0 ? Number(v).toLocaleString() : '');
      caps.forEach((c, i) => {
        const idx = i + 1;
        set(`share_capital_${idx}_currency`, c.currency);
        set(`share_capital_${idx}_issued_ordinary`, numFmt(c.issuedOrdinary));
        set(`share_capital_${idx}_issued_preference`, numFmt(c.issuedPreference));
        set(`share_capital_${idx}_paidup_ordinary`, numFmt(c.paidupOrdinary));
        set(`share_capital_${idx}_paidup_preference`, numFmt(c.paidupPreference));
        set(`share_capital_${idx}_shares_ordinary`, numFmt(c.sharesOrdinary));
        set(`share_capital_${idx}_shares_preference`, numFmt(c.sharesPreference));
      });
    }
    members = Array.isArray(src.members) ? src.members : [];
  } catch (e) { console.warn('[Singapura-Verify] agm-source failed', e); }

  // 2) 이사 (getEntityOfficer director)
  const officersOf = async (group?: 'director' | 'secretary' | 'auditor'): Promise<any[]> => {
    try {
      const res: any = await entityMyApi.getEntityOfficer(cd, group ? { positionGroup: group } : undefined);
      const off: any = res?.data?.data ?? res?.data ?? res ?? {};
      return [...(off.autoOfficers || []), ...(off.manualOfficers || [])].filter((o) => st(o?.cessationDate) === '' && st(o?.status) !== 'Cessation');
    } catch { return []; }
  };
  const dirOfficers = await officersOf('director');
  // 문서 머리 "To :" — 수신 이사(첫 재직 이사). 고객 요청: 비어 있던 칸.
  const firstDir = dirOfficers.map((o) => st(o.fullName || o.contactName)).find(Boolean);
  if (firstDir) set('to_director_name', firstDir);
  await seedPeople('director', 'director_count', dirOfficers.map((o) => ({
    name: st(o.fullName || o.contactName), id_no: st(o.contactRegNo), nationality: st(o.nationalityName),
    appointment_date: isoDate(o.appointmentDate), contactCd: st(o.contactCode || o.contactCd),
  })));

  // 3) 비서
  const secOfficers = await officersOf('secretary');
  await seedPeople('company_secretary', 'secretary_count', secOfficers.map((o) => ({
    name: st(o.fullName || o.contactName), id_no: st(o.contactRegNo), nationality: st(o.nationalityName),
    appointment_date: isoDate(o.appointmentDate), contactCd: st(o.contactCode || o.contactCd),
  })));

  // 3-1) Managing Director / CEO — 전용 조회 그룹이 없어 전체 직책에서 직책명으로 고른다.
  const allOfficers = await officersOf();
  const positionOf = (o: any) => `${st(o?.positionName)} ${st(o?.bizfilePositionHeld)}`;
  const personRow = (o: any) => ({
    name: st(o.fullName || o.contactName), id_no: st(o.contactRegNo), nationality: st(o.nationalityName),
    appointment_date: isoDate(o.appointmentDate), contactCd: st(o.contactCode || o.contactCd),
  });
  await seedPeople('md', 'md_count', allOfficers.filter((o) => /managing\s+director/i.test(positionOf(o))).map(personRow));
  await seedPeople('ceo', 'ceo_count', allOfficers.filter((o) => /chief\s+executive|\bceo\b/i.test(positionOf(o))).map(personRow));

  // 4) 감사인 (있으면) — 주소는 감사법인 컨택트에서
  const audOfficers = await officersOf('auditor');
  await seedPeople('auditor', 'auditor_count', audOfficers.map((o) => ({
    name: '', firm_name: st(o.fullName || o.contactName), uen: st(o.contactRegNo), appointment_date: isoDate(o.appointmentDate),
    contactCd: st(o.contactCode || o.contactCd),
  })));

  // 5) 주주 — 명수만큼 + 주식수 + 지분%
  const named = members.filter((m) => st(m?.name));
  const totalShares = named.reduce((s, m) => s + (Number(m?.sharesTotal) || 0), 0);
  await seedPeople('shareholder', 'shareholder_count', named.map((m) => {
    const shares = Number(m?.sharesTotal) || 0;
    return {
      name: st(m.name), id_no: st(m.registrationNo), contactCd: st(m.contactCd),
      shares_held: shares > 0 ? shares.toLocaleString() : '',
      percent: totalShares > 0 ? String(Math.round((shares / totalShares) * 10000) / 100) : '',
    };
  }));

  autofilledKeys.value = written;
  emitProgress();
}
watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = String(fd.company_name || fd.entityName || '').trim();
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = String(fd.entityCd || fd.icpCd || '').trim();
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || TASK_CODE).trim());
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

function gatherState() { return { values: { ...fill.exportPayload() }, entityCd: entityCd.value, taskTitle: effectiveTitle.value }; }
function restoreState(s: any) {
  if (!s) return;
  restoredFromSaved.value = true;
  if (s.values && typeof s.values === 'object') { for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v)); }
}

function companyFormData(): Record<string, any> {
  return normalizeSingapuraVerificationForWord(fill.exportPayload());
}

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    const companyBase = companyFormData();
    const officerData = {
      entityCd: entityCd.value || null, icpCd: entityCd.value || null,
      presetForm: gatherState(), docType: 'SG_AR_VERIFICATION',
      isMyPreset: props.selection?.isMyPreset === true, formData: companyBase,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskData: { formData: officerData } }) });
      await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskTitle: effectiveTitle.value }) });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null, taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Verification of Data',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => {
            const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
            return { createDocument: steps.length === 0 ? true : steps.includes('doc'), requestTSign: steps.includes('t-sign'), proceedWithEFiling: steps.includes('e-filing') };
          })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; creating.value = false; return; }
    }

    progress.value = 'Preparing documents…';
    // 단일 문서. Edit 재진입 시 selection.documents 가 생성본 다수일 수 있어 tmpId dedupe.
    const seen = new Set<number>();
    const docs = (props.selection.documents || []).filter((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return true; if (seen.has(tid)) return false; seen.add(tid); return true;
    });
    const stripSuffix = (s: string) => s.split(' — ')[0].trim();
    const docsToSave = docs.map((d: any) => {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      const baseLabel = stripSuffix(String(d?.templateName || d?.docCode || 'Verification of Data').replace(/\.docx?$/i, ''));
      return tid ? { templateId: tid, label: baseLabel, personName: String(companyBase.company_name || 'Company'), formData: companyBase } : null;
    }).filter(Boolean) as Array<{ templateId: number; label: string; personName: string; formData: Record<string, any> }>;

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }) }).then((r) => r.json());
        if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
      } catch (err: any) { progress.value = `⚠ Document save failed: ${err?.message || err}`; }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="sv-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Verification of Data' }}</div>
        <div class="co-subtitle">Singapura — AR Verification of Data</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" />
    </div>

    <div class="co-doc">
      <VerificationPackageDoc />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.sv-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sv-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
